/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import CustomerHeader from '../components/CustomerHeader';
import './ServiceProvidersPage.css';

interface ServiceProvider {
  id: string;
  name: string;
  category: string;
  rating: number;
  reviews: number;
  price: string;
  experience: string;
  location: string;
  distance: string;
  available: boolean;
  verified: boolean;
  image?: string;
  skills: string[];
  languages: string[];
  responseTime: string;
}

const ServiceProvidersPage: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'rating' | 'price' | 'distance'>('rating');

  // Category mapping
  const categoryNames: { [key: string]: { en: string; hi: string; icon: string } } = {
    plumbing: { en: 'Plumbing', hi: 'प्लंबिंग', icon: '🔧' },
    electrical: { en: 'Electricals', hi: 'इलेक्ट्रिकल', icon: '⚡' },
    painting: { en: 'Painting', hi: 'पेंटिंग', icon: '🎨' },
    carpenter: { en: 'Carpenter', hi: 'बढ़ई', icon: '🪚' },
    mason: { en: 'Mason / Building', hi: 'राजमिस्त्री', icon: '🧱' },
    'tiles-flooring': { en: 'Tiles & Flooring', hi: 'टाइल्स', icon: '🔲' },
    waterproofing: { en: 'Waterproofing', hi: 'वॉटरप्रूफिंग', icon: '💧' },
    'pest-control': { en: 'Pest Control', hi: 'कीट नियंत्रण', icon: '🐛' },
    'ac-repair': { en: 'AC / Fridge Repair', hi: 'एसी/फ्रिज रिपेयर', icon: '❄️' },
    'ro-service': { en: 'RO Water Service', hi: 'आरओ सर्विस', icon: '💦' },
    'event-decor': { en: 'Event Decorators', hi: 'इवेंट डेकोरेटर', icon: '🎉' },
    'tent-stage': { en: 'Tent & Stage', hi: 'तंबू और स्टेज', icon: '⛺' },
    drivers: { en: 'Drivers', hi: 'ड्राइवर', icon: '🚗' },
    transport: { en: 'Goods Transport', hi: 'माल परिवहन', icon: '🚚' },
    'packers-movers': { en: 'Packers & Movers', hi: 'पैकर्स एंड मूवर्स', icon: '📦' },
    'construction-labor': { en: 'Construction Labor', hi: 'निर्माण मजदूर', icon: '👷' },
    'house-helpers': { en: 'House Helpers', hi: 'घरेलू सहायक', icon: '🏠' },
    loaders: { en: 'Loaders / Unloaders', hi: 'लोडर', icon: '📦' },
    security: { en: 'Security Guards', hi: 'सुरक्षा गार्ड', icon: '🛡️' },
    'home-cleaning': { en: 'Home Cleaning', hi: 'घर की सफाई', icon: '🧹' },
    'tank-cleaning': { en: 'Tank Cleaning', hi: 'टैंक सफाई', icon: '💧' },
    'sofa-cleaning': { en: 'Sofa & Carpet', hi: 'सोफा सफाई', icon: '🛋️' },
    'computer-repair': { en: 'Computer Repair', hi: 'कंप्यूटर रिपेयर', icon: '💻' },
    cctv: { en: 'CCTV Installation', hi: 'सीसीटीवी इंस्टालेशन', icon: '📹' },
    'wifi-setup': { en: 'Wi-Fi Setup', hi: 'वाई-फाई सेटअप', icon: '📶' },
    beautician: { en: 'Beautician', hi: 'ब्यूटीशियन', icon: '💄' },
    tailor: { en: 'Tailor', hi: 'दर्जी', icon: '✂️' },
    laundry: { en: 'Laundry / Ironing', hi: 'कपड़े धोना', icon: '👔' }
  };

  const category = categoryNames[categoryId || ''] || { en: 'Services', hi: 'सेवाएं', icon: '🔧' };

  // Load providers from API
  useEffect(() => {
    const loadProviders = async () => {
      setLoading(true);
      try {
        const providersData = await apiService.getServiceProviders(categoryId, {
          sortBy: sortBy,
          available: undefined // Get all providers, filter by availability in UI
        });
        
        // ALWAYS filter by category on client-side (API might not filter properly)
        let filteredProviders = providersData;
        
        console.log('🔍 FILTER DEBUG: Starting filter process');
        console.log('   categoryId from URL:', categoryId);
        console.log('   providersData type:', Array.isArray(providersData) ? 'array' : typeof providersData);
        console.log('   providersData length:', Array.isArray(providersData) ? providersData.length : 'N/A');
        
        if (categoryId && Array.isArray(providersData) && providersData.length > 0) {
          const beforeCount = providersData.length;
          
          // Debug: Log all categories in response
          const allCategories = Array.from(new Set(providersData.map((p: ServiceProvider) => p.category).filter(Boolean)));
          console.log(`\n📊 API Response Analysis for category "${categoryId}":`);
          console.log(`   Total providers from API: ${beforeCount}`);
          console.log(`   Unique categories in response: [${allCategories.join(', ')}]`);
          
          // Log first few providers for debugging
          console.log('   Sample providers (first 3):');
          providersData.slice(0, 3).forEach((p: ServiceProvider, idx: number) => {
            console.log(`     ${idx + 1}. ${p.name} - category: "${p.category}"`);
          });
          
          if (allCategories.length > 1 || (allCategories.length === 1 && allCategories[0] !== categoryId)) {
            console.warn(`⚠️  API returned providers with different categories!`);
            console.warn(`   Expected: "${categoryId}", Got: [${allCategories.join(', ')}]`);
            console.warn(`   → API is NOT filtering by category properly`);
          }
          
          // STRICT FILTERING with category variations support
          filteredProviders = providersData.filter((provider: ServiceProvider) => {
            if (!provider.category) {
              console.log(`   ❌ EXCLUDED: "${provider.name}" - no category field`);
              return false;
            }
            
            const providerCategory = provider.category.toLowerCase().trim();
            const requestedCategory = categoryId.toLowerCase().trim();
            
            // 1. EXACT MATCH (highest priority)
            if (providerCategory === requestedCategory) {
              console.log(`   ✅ INCLUDED: "${provider.name}" - EXACT match: "${provider.category}" === "${categoryId}"`);
              return true;
            }
            
            // 2. Handle common category variations (database might use different format)
            const categoryVariations: { [key: string]: string[] } = {
              'ac-repair': ['ac_repair', 'acrepair', 'ac repair', 'ac-repair', 'ac/fridge', 'ac-fridge', 'fridge-repair', 'refrigerator-repair'],
              'plumbing': ['plumber', 'plumb'],
              'electrical': ['electric', 'electrician'],
              'painting': ['paint', 'painter'],
              'carpenter': ['carpentry'],
              'home-cleaning': ['cleaning', 'housekeeping', 'home_cleaning'],
              'tiles-flooring': ['tile', 'flooring', 'tiles_flooring'],
              'pest-control': ['pest', 'pest_control'],
              'ro-service': ['ro', 'ro_service', 'water-purifier'],
              'event-decor': ['event', 'decorator', 'event_decor'],
              'tent-stage': ['tent', 'stage', 'tent_stage'],
              'packers-movers': ['packer', 'mover', 'packers_movers'],
              'construction-labor': ['construction', 'labor', 'labour', 'construction_labor'],
              'house-helpers': ['house-helper', 'helper', 'house_helpers'],
              'tank-cleaning': ['tank', 'tank_cleaning'],
              'sofa-cleaning': ['sofa', 'carpet', 'sofa_cleaning'],
              'computer-repair': ['computer', 'laptop', 'computer_repair'],
              'wifi-setup': ['wifi', 'wi-fi', 'wifi_setup'],
              'waterproofing': ['waterproof', 'water_proofing']
            };
            
            // Check if provider category matches any variation
            const variations = categoryVariations[requestedCategory] || [];
            for (const variation of variations) {
              // Normalize variation (remove hyphens/underscores for comparison)
              const normalizedVariation = variation.toLowerCase().replace(/[-_]/g, '');
              const normalizedProvider = providerCategory.replace(/[-_]/g, '');
              
              // Exact match or contains the variation
              if (normalizedProvider === normalizedVariation || 
                  normalizedProvider.includes(normalizedVariation) ||
                  normalizedVariation.includes(normalizedProvider)) {
                console.log(`   ✅ INCLUDED: "${provider.name}" - variation match: "${provider.category}" matches "${variation}" for "${categoryId}"`);
                return true;
              }
            }
            
            // 3. Also check if category contains key words (for ac-repair: "ac", "fridge", "repair")
            const categoryKeywords: { [key: string]: string[] } = {
              'ac-repair': ['ac', 'fridge', 'refrigerator', 'repair'],
              'plumbing': ['plumb', 'pipe', 'water'],
              'electrical': ['electric', 'wire'],
              'painting': ['paint'],
              'carpenter': ['carpent', 'wood', 'furniture'],
              'home-cleaning': ['clean', 'housekeep'],
              'tiles-flooring': ['tile', 'floor'],
              'pest-control': ['pest', 'rodent', 'insect'],
              'ro-service': ['ro', 'purifier'],
              'computer-repair': ['computer', 'laptop', 'pc'],
              'wifi-setup': ['wifi', 'internet'],
              'waterproofing': ['waterproof']
            };
            
            const keywords = categoryKeywords[requestedCategory] || [];
            const matchesKeyword = keywords.some(keyword => {
              // Check if provider category contains the keyword
              return providerCategory.includes(keyword) && keyword.length >= 3; // Only substantial keywords (3+ chars)
            });
            
            if (matchesKeyword) {
              console.log(`   ✅ INCLUDED: "${provider.name}" - keyword match: "${provider.category}" contains keywords for "${categoryId}"`);
              return true;
            }
            
            // No match - exclude
            console.log(`   ❌ EXCLUDED: "${provider.name}" - category "${provider.category}" doesn't match "${categoryId}"`);
            console.log(`      Tried variations: [${variations.join(', ')}]`);
            console.log(`      Tried keywords: [${keywords.join(', ')}]`);
            return false;
          });
          
          // Always log filtering results
          const afterCount = filteredProviders.length;
          console.log(`\n✅ Category Filter Results: ${beforeCount} → ${afterCount} providers for "${categoryId}"`);
          
          if (beforeCount === afterCount && beforeCount > 0) {
            console.error(`\n❌ CRITICAL: All ${beforeCount} providers passed the filter!`);
            console.error('   This means the filter is NOT working correctly.');
            console.error('   Possible causes:');
            console.error('   1. All providers have the same category (unlikely)');
            console.error('   2. Filter logic is matching everything');
            console.error('   3. API is returning pre-filtered data');
            console.error(`   Categories in response: [${allCategories.join(', ')}]`);
            console.error('   First 5 provider categories:', 
              providersData.slice(0, 5).map((p: ServiceProvider) => `"${p.category}"`).join(', '));
          } else if (afterCount === 0 && beforeCount > 0) {
            console.warn(`\n⚠️  WARNING: Filter removed ALL providers!`);
            console.warn(`   Requested category: "${categoryId}"`);
            console.warn(`   Provider categories found: [${allCategories.join(', ')}]`);
            console.warn(`   → Category mismatch - backend uses different category names`);
          } else {
            console.log(`\n✅ Filter working correctly: ${beforeCount} providers → ${afterCount} matching providers`);
          }
        } else if (!categoryId) {
          console.log('📊 No category filter - showing all providers');
        } else if (!Array.isArray(providersData)) {
          console.error('❌ ERROR: providersData is not an array:', typeof providersData);
        } else if (providersData.length === 0) {
          console.log('📊 No providers returned from API');
        }
        
        setProviders(filteredProviders);
      } catch (error: any) {
        console.error('Error loading providers:', error);
        // Fallback to empty array - API will use mock data if available
        setProviders([]);
      } finally {
        setLoading(false);
      }
    };

    loadProviders();
  }, [categoryId, sortBy]);

  const handleProviderClick = (providerId: string) => {
    navigate(`/services/provider/${providerId}`);
  };

  const handleBookNow = (providerId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/services/book/${providerId}`);
  };

  const handleRequestCall = (providerId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    // Open phone dialer or WhatsApp
    window.open(`tel:+919876543210`, '_self');
  };

  const sortedProviders = [...providers].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'distance') {
      const aDist = parseFloat(a.distance);
      const bDist = parseFloat(b.distance);
      return aDist - bDist;
    }
    // Sort by price (extract first number)
    const aPrice = parseInt(a.price.replace(/[^0-9]/g, ''));
    const bPrice = parseInt(b.price.replace(/[^0-9]/g, ''));
    return aPrice - bPrice;
  });

  return (
    <div className="service-providers-page">
      {/* Header */}
      <CustomerHeader
        variant="sticky"
        showTagline
        showVoiceSearch
        showProfileDropdown
        showBackButton={true}
        onBackClick={() => navigate('/services')}
        onSearch={(query) => {
          navigate(`/search?q=${encodeURIComponent(query)}`);
        }}
        onCartClick={() => navigate('/cart')}
      />

      {/* Main Content */}
      <main className="service-providers-main">
        <div className="service-providers-container">
          {/* Breadcrumb Navigation */}
          <nav className="service-breadcrumbs" aria-label="Breadcrumb">
            <button 
              className="breadcrumb-link"
              onClick={() => navigate('/app')}
              aria-label="Home"
            >
              {language === 'hi' ? 'होम' : 'Home'}
            </button>
            <span className="breadcrumb-separator" aria-hidden="true">›</span>
            <button 
              className="breadcrumb-link"
              onClick={() => navigate('/services')}
              aria-label="Services"
            >
              {language === 'hi' ? 'सेवाएं' : 'Services'}
            </button>
            <span className="breadcrumb-separator" aria-hidden="true">›</span>
            <span className="breadcrumb-current" aria-current="page">
              {language === 'hi' ? category.hi : category.en}
            </span>
          </nav>

          {/* Category Header with Back Button */}
          <div className="category-header-section">
            <button 
              className="back-to-services-btn"
              onClick={() => navigate('/services')}
              aria-label="Back to Services"
            >
              <span className="back-icon">←</span>
              <span className="back-text">
                {language === 'hi' ? 'सेवाएं पर वापस जाएं' : 'Back to Services'}
              </span>
            </button>
            <div className="category-title-section">
              <span className="category-icon-large">{category.icon}</span>
              <h1 className="category-title">
                {language === 'hi' ? category.hi : category.en}
              </h1>
            </div>
          </div>

          {/* Sort & Filter Bar */}
          <div className="sort-filter-bar">
            <div className="sort-options">
              <span className="sort-label">
                {language === 'hi' ? 'क्रमबद्ध करें:' : 'Sort by:'}
              </span>
              <button
                className={`sort-btn ${sortBy === 'rating' ? 'active' : ''}`}
                onClick={() => setSortBy('rating')}
              >
                {language === 'hi' ? 'रेटिंग' : 'Rating'}
              </button>
              <button
                className={`sort-btn ${sortBy === 'price' ? 'active' : ''}`}
                onClick={() => setSortBy('price')}
              >
                {language === 'hi' ? 'कीमत' : 'Price'}
              </button>
              <button
                className={`sort-btn ${sortBy === 'distance' ? 'active' : ''}`}
                onClick={() => setSortBy('distance')}
              >
                {language === 'hi' ? 'दूरी' : 'Distance'}
              </button>
            </div>
          </div>

          {/* Providers List */}
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>{language === 'hi' ? 'लोड हो रहा है...' : 'Loading providers...'}</p>
            </div>
          ) : (
            <div className="providers-list">
              {sortedProviders.map((provider) => (
                <article
                  key={provider.id}
                  className="provider-card"
                  onClick={() => handleProviderClick(provider.id)}
                >
                  <div className="provider-card-header">
                    <div className="provider-avatar">
                      {provider.image ? (
                        <img src={provider.image} alt={provider.name} />
                      ) : (
                        <span className="avatar-initial">
                          {provider.name.charAt(0)}
                        </span>
                      )}
                      {provider.verified && (
                        <span className="verified-badge" title="Verified">✓</span>
                      )}
                    </div>
                    <div className="provider-info">
                      <div className="provider-name-row">
                        <h3 className="provider-name">{provider.name}</h3>
                        {!provider.available && (
                          <span className="unavailable-badge">
                            {language === 'hi' ? 'अनुपलब्ध' : 'Busy'}
                          </span>
                        )}
                      </div>
                      <div className="provider-rating">
                        <span className="rating-stars">⭐</span>
                        <span className="rating-value">{provider.rating}</span>
                        <span className="rating-reviews">
                          ({provider.reviews} {language === 'hi' ? 'समीक्षा' : 'reviews'})
                        </span>
                      </div>
                      <div className="provider-meta">
                        <span className="meta-item">
                          📍 {provider.location} • {provider.distance}
                        </span>
                        <span className="meta-item">
                          💼 {provider.experience} {language === 'hi' ? 'अनुभव' : 'experience'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="provider-skills">
                    {provider.skills.slice(0, 3).map((skill, idx) => (
                      <span key={idx} className="skill-tag">
                        {skill}
                      </span>
                    ))}
                  </div>

                  <div className="provider-card-footer">
                    <div className="provider-pricing">
                      <span className="price-label">
                        {language === 'hi' ? 'कीमत:' : 'Price:'}
                      </span>
                      <span className="price-value">{provider.price}</span>
                      <span className="response-time">
                        ⏱️ {provider.responseTime}
                      </span>
                    </div>
                    <div className="provider-actions">
                      <button
                        className="request-call-btn"
                        onClick={(e) => handleRequestCall(provider.id, e)}
                        aria-label="Request Call"
                      >
                        📞 {language === 'hi' ? 'कॉल करें' : 'Call'}
                      </button>
                      <button
                        className="book-now-btn"
                        onClick={(e) => handleBookNow(provider.id, e)}
                        disabled={!provider.available}
                        aria-label="Book Now"
                      >
                        {language === 'hi' ? 'बुक करें' : 'Book Now'}
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ServiceProvidersPage;








